import type { IModuleField, IRecordValueInput } from '@/features/module-builder/types';
import { evaluateDependencies } from './dependencies';
import { isWritableField, type RecordFormValues } from './schema';

/**
 * Conversions between record snapshots / API payloads and form values
 * (keyed by field NAME).
 */

/** ISO string → `yyyy-MM-dd` for `<input type="date">`. */
function toDateInput(value: unknown): string {
    if (typeof value !== 'string' || value.length < 10) return '';
    return value.slice(0, 10);
}

/** ISO string → `yyyy-MM-ddTHH:mm` (local) for `<input type="datetime-local">`. */
function toDateTimeInput(value: unknown): string {
    if (typeof value !== 'string') return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The form value a field starts with when neither source nor default apply. */
function emptyFormValue(field: IModuleField): unknown {
    switch (field.fieldType) {
        case 'BOOLEAN':
            return false;
        case 'MULTI_SELECT':
            return [];
        case 'RATING':
            return 0;
        default:
            return '';
    }
}

/** Snapshot/API value → form value for one field. */
export function toFormValue(field: IModuleField, value: unknown): unknown {
    if (value === null || value === undefined) return emptyFormValue(field);
    switch (field.fieldType) {
        case 'BOOLEAN':
            return value === true || value === 'true';
        case 'MULTI_SELECT':
            return Array.isArray(value) ? value.map((v) => String(v)) : [];
        case 'RATING': {
            const num = Number(value);
            return Number.isFinite(num) ? num : 0;
        }
        case 'DATE':
            return toDateInput(value);
        case 'DATETIME':
            return toDateTimeInput(value);
        case 'LOOKUP':
        case 'USER':
            // Single association in v1 UI; arrays render as their first id.
            return Array.isArray(value) ? String(value[0] ?? '') : String(value);
        default:
            return String(value);
    }
}

/**
 * Build the form's default values for a field set, seeded from a snapshot
 * (edit) or from field defaults (create).
 */
export function buildFormDefaults(
    fields: IModuleField[],
    source?: Record<string, unknown> | null,
): RecordFormValues {
    const defaults: RecordFormValues = {};
    for (const field of fields) {
        if (!isWritableField(field)) continue;
        const sourceValue = source?.[field.name];
        if (sourceValue !== undefined && sourceValue !== null) {
            defaults[field.name] = toFormValue(field, sourceValue);
        } else if (!source && field.defaultValue) {
            // Field defaults apply on create only (no source record).
            defaults[field.name] = toFormValue(field, field.defaultValue);
        } else {
            defaults[field.name] = emptyFormValue(field);
        }
    }
    return defaults;
}

/** Form value → API value for one field (`null` clears the stored value). */
function toApiValue(field: IModuleField, value: unknown): unknown {
    if (value === null || value === undefined || value === '') return null;
    switch (field.fieldType) {
        case 'BOOLEAN':
            return value === true;
        case 'MULTI_SELECT':
            return Array.isArray(value) && value.length > 0 ? value : null;
        case 'NUMBER':
        case 'CURRENCY':
        case 'PERCENTAGE': {
            const num = Number(value);
            return Number.isFinite(num) ? num : null;
        }
        case 'RATING': {
            const num = Number(value);
            return Number.isFinite(num) && num > 0 ? num : null;
        }
        case 'DATETIME': {
            const date = new Date(String(value));
            return Number.isNaN(date.getTime()) ? null : date.toISOString();
        }
        default:
            return value;
    }
}

/**
 * Serialize form values into the `{ fieldId, value }` payload. Dependency-
 * hidden fields, read-only fields, AUTO_INCREMENT, and SYSTEM-source fields
 * are skipped — mirroring what the backend would reject or drop anyway.
 */
export function toRecordValueInputs(
    fields: IModuleField[],
    values: RecordFormValues,
): IRecordValueInput[] {
    const writable = fields.filter(isWritableField);
    const { hidden } = evaluateDependencies(writable, values);
    const inputs: IRecordValueInput[] = [];
    for (const field of writable) {
        if (hidden.has(field.name)) continue;
        inputs.push({ fieldId: field.id, value: toApiValue(field, values[field.name]) });
    }
    return inputs;
}
